import { useEffect, useState } from "react";
import { http } from "@/lib/http";

const urlBase64ToUint8Array = (v) => {
  const pad = "=".repeat((4 - (v.length % 4)) % 4);
  const b64 = (v + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

export const usePush = () => {
  const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const [permission, setPermission] = useState(supported ? Notification.permission : "denied");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!supported) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch (e) {
        setSubscribed(false);
      }
    })();
  }, [supported]);

  const enable = async () => {
    if (!supported) return { ok: false, reason: "not_supported" };
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return { ok: false, reason: "denied" };
      const { data: keyData } = await http.get("/push/vapid-public-key");
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(keyData.key),
        });
      }
      await http.post("/push/subscribe", sub.toJSON());
      setSubscribed(true);
      return { ok: true };
    } catch (e) {
      return { ok: false, reason: e?.message || "error" };
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    if (!supported) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await http.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } finally {
      setLoading(false);
    }
  };

  const test = async () => {
    try { await http.post("/push/test", {}); return true; } catch { return false; }
  };

  return { supported, permission, subscribed, loading, enable, disable, test };
};
